"use client";
import type { ReactNode } from "react";
import type { Control } from "react-hook-form";
import { MdSpeed } from "react-icons/md";
import { FormattedMessage } from "react-intl";
import { CustomTooltip, FormField, FormItem, FormMessage, Input, Slider } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import { InfoCircle } from "iconoir-react";

import type { SdlBuilderFormValuesType, ServiceType } from "@src/types";
import { validationConfig } from "@src/utils/akash/units";
import { FormPaper } from "./FormPaper";

type Props = {
  serviceIndex: number;
  children?: ReactNode;
  control: Control<SdlBuilderFormValuesType, any>;
  currentService: ServiceType;
};

export const CpuFormControl: React.FunctionComponent<Props> = ({ control, serviceIndex }) => {
  return (
    <FormField
      control={control}
      name={`services.${serviceIndex}.profile.cpu`}
      render={({ field, fieldState }) => (
        <FormPaper>
          <FormItem>
            <div className="flex items-center">
              <div className="flex items-center">
                <MdSpeed className="mr-2 text-2xl text-muted-foreground" />
                <strong className="text-sm">
                  <FormattedMessage id="sdl.cpuformcontrol.label" defaultMessage="CPU" />
                </strong>

                <CustomTooltip
                  title={
                    <>
                      <FormattedMessage id="sdl.cpuformcontrol.tooltip1" defaultMessage="The amount of vCPU's required for this workload." />
                      <br />
                      <br />
                      <FormattedMessage
                        id="sdl.cpuformcontrol.tooltip2"
                        defaultMessage="The maximum for a single instance is {max} vCPU's."
                        values={{ max: validationConfig.maxCpuAmount }}
                      />
                      <br />
                      <br />
                      <FormattedMessage
                        id="sdl.cpuformcontrol.tooltip3"
                        defaultMessage="The maximum total multiplied by the count of instances is 512 vCPU's."
                      />
                    </>
                  }
                >
                  <InfoCircle className="ml-2 text-xs text-muted-foreground" />
                </CustomTooltip>
              </div>
              <Input
                type="number"
                color="secondary"
                error={!!fieldState.error}
                value={field.value || ""}
                onChange={event => field.onChange(parseFloat(event.target.value))}
                min={0.1}
                step={0.1}
                max={validationConfig.maxCpuAmount}
                inputClassName="ml-4 w-[100px]"
              />
            </div>

            <div className="pt-2">
              <Slider
                value={[field.value || 0]}
                min={0.1}
                max={validationConfig.maxCpuAmount}
                step={1}
                color="secondary"
                aria-label="CPU"
                onValueChange={newValue => field.onChange(newValue[0])}
              />
            </div>

            <FormMessage className={cn({ "pt-2": !!fieldState.error })} />
          </FormItem>
        </FormPaper>
      )}
    />
  );
};
